import React, { useState, useEffect, useCallback, useRef } from "react";
import { CommonInput } from "@/components/ui/common-input";
import { CommonSelect } from "@/components/ui/common-select";
import { CommonButton } from "@/components/ui/common-button";
import { DateTimeInput } from "@/components/ui/DateTimeInput";
import { useToast } from "@/components/ui/use-toast";
import { Plus, Truck, Calendar, FileText, AlertCircle, MapPin } from "lucide-react";
import tripService from "@/services/tripService";
import rakeMovementService from "@/services/rakeMovementService";
import masterDataService from "@/services/masterDataService";
import productService from "@/services/productService";
import rakePointService from "@/services/rakePointService";
import { useAuth } from "@/contexts/AuthContext";
import Loader from "@/components/ui/loader";
import { DialogFooter } from "@/components/ui/dialog";

import GoogleMapSelector from "@/components/tripManagement/addtrip/GoogleMapSelector";
import SecondaryDispatchItemCard from "./SecondaryDispatchItemCard";

const SecondaryDispatchForm = ({ parentTripId, onCancel, onSuccess }) => {
  const { toast } = useToast();
  const { userProfile } = useAuth();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [parentTrip, setParentTrip] = useState(null);
  const [balanceData, setBalanceData] = useState(null);

  const [transporters, setTransporters] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [godowns, setGodowns] = useState([]);
  const [states, setStates] = useState([]);
  const [productTypes, setProductTypes] = useState([]);

  const [districtsByState, setDistrictsByState] = useState({});
  const [distributorsByDistrict, setDistributorsByDistrict] = useState({});
  const [retailersByDistrict, setRetailersByDistrict] = useState({});

  const [loadingDropdowns, setLoadingDropdowns] = useState({
    transporters: false,
    drivers: false,
    vehicles: false,
    godowns: false,
    states: false,
  });
  const [loadingDistricts, setLoadingDistricts] = useState({});
  const [loadingDistributors, setLoadingDistributors] = useState({});
  const [loadingRetailers, setLoadingRetailers] = useState({});

  const [transporterSearch, setTransporterSearch] = useState("");
  const [driverSearch, setDriverSearch] = useState("");
  const [vehicleSearch, setVehicleSearch] = useState("");

  const [formData, setFormData] = useState({
    transporter_id: "",
    driver_id: "",
    vehicle_id: "",
    scheduled_start_time: "",
    scheduled_end_time: "",
    billed_to_party: "",
    shipped_to_party: "",
    shipped_to_override: false,
    start_latitude: "",
    start_longitude: "",
  });

  const [items, setItems] = useState([
    {
      id: Date.now(),
      destinationType: "distributor",
      state_id: "",
      district_id: "",
      distributor_id: "",
      retailer_id: "",
      rake_point_id: "",
      product_type_id: "",
      product_quantity: "",
      source_trip_item_id: "",
      end_latitude: "",
      end_longitude: "",
    },
  ]);

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (parentTripId) {
      loadData();
    }
  }, [parentTripId]);

  const fetchTransporters = async (search = "") => {
    try {
      setLoadingDropdowns((prev) => ({ ...prev, transporters: true }));
      const data = await masterDataService.getTransporters(0, 10, false, search);
      setTransporters(data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingDropdowns((prev) => ({ ...prev, transporters: false }));
    }
  };

  const fetchDrivers = async (search = "") => {
    try {
      setLoadingDropdowns((prev) => ({ ...prev, drivers: true }));
      const data = await masterDataService.getDrivers(0, 10, false, false, search);
      setDrivers(data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingDropdowns((prev) => ({ ...prev, drivers: false }));
    }
  };

  const fetchVehicles = async (search = "") => {
    try {
      setLoadingDropdowns((prev) => ({ ...prev, vehicles: true }));
      const data = await masterDataService.getVehicles(0, 10, false, false, search);
      setVehicles(data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingDropdowns((prev) => ({ ...prev, vehicles: false }));
    }
  };

  const fetchDropdownGodowns = async () => {
    try {
      setLoadingDropdowns((prev) => ({ ...prev, godowns: true }));
      const data = await rakePointService.getRakePointsDropdown("GODOWN");
      setGodowns(data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingDropdowns((prev) => ({ ...prev, godowns: false }));
    }
  };

  const fetchStates = async () => {
    try {
      setLoadingDropdowns((prev) => ({ ...prev, states: true }));
      const data = await masterDataService.getStates(true);
      setStates(data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingDropdowns((prev) => ({ ...prev, states: false }));
    }
  };

  const fetchProductTypes = async () => {
    try {
      const data = await productService.getProducts(0, 100);
      setProductTypes(data || []);
    } catch (err) {
      console.error(err);
    }
  };


  const fetchDistrictsForState = async (stateId) => {
    if (!stateId || districtsByState[stateId]) return;
    try {
      setLoadingDistricts((prev) => ({ ...prev, [stateId]: true }));
      const data = await masterDataService.getDistrictsByState(stateId, true);
      setDistrictsByState((prev) => ({ ...prev, [stateId]: data || [] }));
    } catch (err) {
      console.error("Failed to load districts:", err);
    } finally {
      setLoadingDistricts((prev) => ({ ...prev, [stateId]: false }));
    }
  };

  const fetchDistributorsForDistrict = async (districtId) => {
    if (!districtId || distributorsByDistrict[districtId]) return;
    try {
      setLoadingDistributors((prev) => ({ ...prev, [districtId]: true }));
      const data = await masterDataService.getDistributorsByDistrict(districtId, "", 0, 50, false);
      setDistributorsByDistrict((prev) => ({ ...prev, [districtId]: data || [] }));
    } catch (err) {
      console.error("Failed to load distributors:", err);
    } finally {
      setLoadingDistributors((prev) => ({ ...prev, [districtId]: false }));
    }
  };

  const fetchRetailersForDistrict = async (districtId) => {
    if (!districtId || retailersByDistrict[districtId]) return;
    try {
      setLoadingRetailers((prev) => ({ ...prev, [districtId]: true }));
      const data = await masterDataService.getRetailers(0, 1000, false, districtId);
      const filteredData = (data || []).filter(item => item.district_id === districtId);
      setRetailersByDistrict((prev) => ({ ...prev, [districtId]: filteredData }));
    } catch (err) {
      console.error("Failed to load retailers:", err);
    } finally {
      setLoadingRetailers((prev) => ({ ...prev, [districtId]: false }));
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setErrors({});

      const [movement, balance] = await Promise.all([
        rakeMovementService.getRakeMovementById(parentTripId).catch(() => tripService.getTripById(parentTripId)),
        tripService.getDispatchBalance(parentTripId).catch((err) => {
          console.warn("Failed to fetch dispatch balance from backend, using client-side fallback:", err);
          return null;
        }),
      ]);

      const trip = {
        ...movement,
        billed_to_party: movement.billed_to_party || "",
        shipped_to_party: movement.shipped_to_party || "",
        items: (movement.items || []).map(item => ({
          ...item,
          product_quantity: item.product_quantity || item.quantity_mt || 0
        }))
      };

      setParentTrip(trip);
      setBalanceData(balance);

      const initialStartLat = trip.start_latitude?.toString() || trip.rake_point?.latitude?.toString() || trip.origin_rake_point?.latitude?.toString() || "";
      const initialStartLng = trip.start_longitude?.toString() || trip.rake_point?.longitude?.toString() || trip.origin_rake_point?.longitude?.toString() || "";

      setFormData({
        transporter_id: "",
        driver_id: "",
        vehicle_id: "",
        scheduled_start_time: "",
        scheduled_end_time: "",
        billed_to_party: trip.billed_to_party || "",
        shipped_to_party: trip.shipped_to_party || "",
        shipped_to_override: false,
        start_latitude: initialStartLat,
        start_longitude: initialStartLng,
      });

      const availableItems = (trip.items || []).filter((item) => {
        const balanceLine = balance?.lines?.find((l) => l.rake_movement_item_id === item.id || l.trip_item_id === item.id);
        return !balanceLine || balanceLine.remaining_qty_mt > 0;
      });

      const firstAvailableItem = availableItems[0] || null;
      const destType = firstAvailableItem?.distributor_id ? "distributor" : firstAvailableItem?.retailer_id ? "retailer" : firstAvailableItem?.rake_point_id ? "godown" : "distributor";

      let initialSId = "";
      let initialDId = "";

      if (firstAvailableItem) {
        if (destType === "distributor" && firstAvailableItem?.distributor) {
          initialSId = firstAvailableItem.distributor.state_id || "";
          initialDId = firstAvailableItem.distributor.district_id || "";
        } else if (destType === "retailer" && firstAvailableItem?.retailer) {
          initialSId = firstAvailableItem.retailer.state_id || "";
          initialDId = firstAvailableItem.retailer.district_id || "";
        }
      }

      const balanceLine = firstAvailableItem
        ? balance?.lines?.find((l) => l.rake_movement_item_id === firstAvailableItem.id || l.trip_item_id === firstAvailableItem.id)
        : null;
      const prefillQty = balanceLine?.remaining_qty_mt !== undefined && balanceLine?.remaining_qty_mt !== null
        ? String(balanceLine.remaining_qty_mt)
        : (firstAvailableItem ? String(trip.receipt_qty || firstAvailableItem.product_quantity || firstAvailableItem.quantity_mt || "") : "");

      const initialEndLat = firstAvailableItem?.distributor?.latitude?.toString() || firstAvailableItem?.retailer?.latitude?.toString() || firstAvailableItem?.rake_point?.latitude?.toString() || "";
      const initialEndLng = firstAvailableItem?.distributor?.longitude?.toString() || firstAvailableItem?.retailer?.longitude?.toString() || firstAvailableItem?.rake_point?.longitude?.toString() || "";

      setItems([
        {
          id: Date.now(),
          destinationType: firstAvailableItem ? destType : "distributor",
          state_id: initialSId,
          district_id: initialDId,
          distributor_id: firstAvailableItem?.distributor_id || "",
          retailer_id: firstAvailableItem?.retailer_id || "",
          rake_point_id: firstAvailableItem?.rake_point_id || "",
          product_type_id: firstAvailableItem?.product_type_id || "",
          product_quantity: prefillQty,
          source_trip_item_id: firstAvailableItem?.id || "",
          consignee_name: "",
          consignee_contact_person: "",
          consignee_phone: "",
          consignee_email: "",
          consignee_address: "",
          invoice_number: "",
          dc_number: "",
          estimated_kms: "",
          end_latitude: initialEndLat,
          end_longitude: initialEndLng,
        },
      ]);

      const delay = (ms) => new Promise((r) => setTimeout(r, ms));
      await fetchTransporters(""); await delay(200);
      await fetchDrivers(""); await delay(200);
      await fetchVehicles(""); await delay(200);
      await fetchDropdownGodowns(); await delay(200);
      await fetchStates(); await delay(200);
      await fetchProductTypes();

      if (initialSId) {
        fetchDistrictsForState(initialSId);
      }
      if (initialDId) {
        if (destType === "distributor") {
          fetchDistributorsForDistrict(initialDId);
        } else if (destType === "retailer") {
          fetchRetailersForDistrict(initialDId);
        }
      }

    } catch (err) {
      console.error("Failed to load secondary dispatch data:", err);
      toast({
        title: "Error",
        description: "Failed to load detailed trip or balance information.",
        variant: "destructive",
      });
      if (onCancel) onCancel();
    } finally {
      setLoading(false);
    }
  };

  const timerRef = useRef({});
  const handleSearchChange = (field, value) => {
    if (field === "transporter") {
      setTransporterSearch(value);
      clearTimeout(timerRef.current.transporter);
      timerRef.current.transporter = setTimeout(() => fetchTransporters(value), 300);
    } else if (field === "driver") {
      setDriverSearch(value);
      clearTimeout(timerRef.current.driver);
      timerRef.current.driver = setTimeout(() => fetchDrivers(value), 300);
    } else if (field === "vehicle") {
      setVehicleSearch(value);
      clearTimeout(timerRef.current.vehicle);
      timerRef.current.vehicle = setTimeout(() => fetchVehicles(value), 300);
    }
  };

  useEffect(() => {
    return () => {
      Object.values(timerRef.current).forEach(clearTimeout);
    };
  }, []);

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const addItemRow = () => {
    setItems((prev) => [
      ...prev,
      {
        id: Date.now(),
        destinationType: "distributor",
        state_id: "",
        district_id: "",
        distributor_id: "",
        retailer_id: "",
        rake_point_id: "",
        product_type_id: "",
        product_quantity: "",
        source_trip_item_id: "",
        consignee_name: "",
        consignee_contact_person: "",
        consignee_phone: "",
        consignee_email: "",
        consignee_address: "",
        invoice_number: "",
        dc_number: "",
        estimated_kms: "",
        end_latitude: "",
        end_longitude: "",
      },
    ]);
  };

  const removeItemRow = (id) => {
    if (items.length === 1) return;
    setItems((prev) => prev.filter((item) => item.id !== id));
  };

  const updateItemRow = (id, fieldOrUpdates, value) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.id === id) {
          const isBatch = typeof fieldOrUpdates === "object" && fieldOrUpdates !== null;
          const updated = isBatch
            ? { ...item, ...fieldOrUpdates }
            : { ...item, [fieldOrUpdates]: value };

          const field = isBatch ? null : fieldOrUpdates;

          if (field === "destinationType") {
            updated.state_id = "";
            updated.district_id = "";
            updated.distributor_id = "";
            updated.retailer_id = "";
            updated.rake_point_id = "";
            updated.end_latitude = "";
            updated.end_longitude = "";
          } else if (field === "state_id") {
            updated.district_id = "";
            updated.distributor_id = "";
            updated.retailer_id = "";
            updated.end_latitude = "";
            updated.end_longitude = "";
            if (value) fetchDistrictsForState(value);
          } else if (field === "district_id") {
            updated.distributor_id = "";
            updated.retailer_id = "";
            updated.end_latitude = "";
            updated.end_longitude = "";
            if (value) {
              if (item.destinationType === "distributor") fetchDistributorsForDistrict(value);
              else if (item.destinationType === "retailer") fetchRetailersForDistrict(value);
            }
          } else if (field === "distributor_id") {
            const dists = distributorsByDistrict[item.district_id] || [];
            const found = dists.find((d) => d.id === value);
            if (found?.latitude && found?.longitude) {
              updated.end_latitude = found.latitude.toString();
              updated.end_longitude = found.longitude.toString();
            }
          } else if (field === "retailer_id") {
            const rets = retailersByDistrict[item.district_id] || [];
            const found = rets.find((r) => r.id === value);
            if (found?.latitude && found?.longitude) {
              updated.end_latitude = found.latitude.toString();
              updated.end_longitude = found.longitude.toString();
            }
          } else if (field === "rake_point_id") {
            const found = godowns.find((g) => g.id === value);
            if (found?.latitude && found?.longitude) {
              updated.end_latitude = found.latitude.toString();
              updated.end_longitude = found.longitude.toString();
            }
          }
          return updated;
        }
        return item;
      })
    );
  };



  const validateForm = () => {
    const newErrors = {};

    if (!formData.transporter_id) newErrors.transporter_id = "Transporter is required";
    if (!formData.driver_id) newErrors.driver_id = "Driver is required";
    if (!formData.vehicle_id) newErrors.vehicle_id = "Vehicle is required";
    if (!formData.scheduled_start_time) newErrors.scheduled_start_time = "Start time is required";
    if (!formData.scheduled_end_time) newErrors.scheduled_end_time = "End time is required";

    if (formData.scheduled_start_time && formData.scheduled_end_time) {
      const start = new Date(formData.scheduled_start_time);
      const end = new Date(formData.scheduled_end_time);
      if (end <= start) {
        newErrors.scheduled_end_time = "End time must be after start time";
      }
    }

    const itemErrors = [];
    let totalQty = 0;

    items.forEach((item, index) => {
      const errorsObj = {};
      if (item.destinationType === "distributor") {
        if (!item.state_id) errorsObj.state_id = "State is required";
        if (!item.district_id) errorsObj.district_id = "District is required";
        if (!item.distributor_id) errorsObj.target_id = "Distributor is required";
      } else if (item.destinationType === "retailer") {
        if (!item.state_id) errorsObj.state_id = "State is required";
        if (!item.district_id) errorsObj.district_id = "District is required";
        if (!item.retailer_id) errorsObj.target_id = "Retailer is required";
      } else if (item.destinationType === "godown" && !item.rake_point_id) {
        errorsObj.target_id = "Godown location is required";
      }

      if (!item.product_type_id) {
        errorsObj.product_type_id = "Product is required";
      }

      if (!item.consignee_name?.trim()) errorsObj.consignee_name = "Consignee name is required";
      if (!item.consignee_address?.trim()) errorsObj.consignee_address = "Address is required";
      if (!item.consignee_phone?.trim()) {
        errorsObj.consignee_phone = "Phone number is required";
      } else if (!/^[0-9]{10}$/.test(item.consignee_phone.trim())) {
        errorsObj.consignee_phone = "Phone must be exactly 10 digits";
      }
      if (!item.invoice_number?.trim()) errorsObj.invoice_number = "Invoice number is required";
      if (!item.dc_number?.trim()) errorsObj.dc_number = "DC number is required";

      const qty = parseFloat(item.product_quantity);
      if (!item.product_quantity) {
        errorsObj.product_quantity = "Quantity is required";
      } else if (isNaN(qty) || qty <= 0) {
        errorsObj.product_quantity = "Quantity must be positive";
      } else {
        totalQty += qty;

        if (item.source_trip_item_id && balanceData?.lines) {
          const matchLine = balanceData.lines.find(l => l.trip_item_id === item.source_trip_item_id);
          if (matchLine && qty > matchLine.remaining_qty_mt) {
            errorsObj.product_quantity = `Exceeds line remaining (${matchLine.remaining_qty_mt.toFixed(2)} MT)`;
          }
        }
      }

      if (Object.keys(errorsObj).length > 0) {
        itemErrors[index] = errorsObj;
      }
    });

    if (itemErrors.length > 0) {
      newErrors.items = itemErrors;
    }

    if (balanceData) {
      const maxRemaining = balanceData.remaining_qty_mt || 0;
      if (totalQty > maxRemaining) {
        newErrors.totalQty = `Total quantity (${totalQty.toFixed(2)} MT) exceeds remaining (${maxRemaining.toFixed(2)} MT)`;
      }
    } else {
      const maxRemaining = parentTrip?.receipt_qty || parentTrip?.baseline_qty_mt || 0;
      if (totalQty > maxRemaining) {
        newErrors.totalQty = `Total quantity (${totalQty.toFixed(2)} MT) exceeds parent receipt/baseline (${maxRemaining.toFixed(2)} MT)`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setSubmitting(true);

      const itemsPayload = items.map((item, idx) => {
        const payloadItem = {
          product_type_id: item.product_type_id,
          product_quantity: parseFloat(item.product_quantity),
          item_sequence: idx + 1,
        };

        if (item.end_latitude && item.end_longitude) {
          payloadItem.end_latitude = parseFloat(item.end_latitude);
          payloadItem.end_longitude = parseFloat(item.end_longitude);
        }

        if (item.destinationType === "distributor") {
          payloadItem.distributor_id = item.distributor_id;
        } else if (item.destinationType === "retailer") {
          payloadItem.retailer_id = item.retailer_id;
        } else if (item.destinationType === "godown") {
          payloadItem.rake_point_id = item.rake_point_id;
        }

        if (item.source_trip_item_id) {
          payloadItem.source_rake_movement_item_id = item.source_trip_item_id;

          const matchItem = parentTrip?.items?.find((i) => i.id === item.source_trip_item_id);
          if (matchItem) {
            if (matchItem.consignee_name) payloadItem.consignee_name = matchItem.consignee_name;
            if (matchItem.consignee_phone) payloadItem.consignee_phone = matchItem.consignee_phone;
            if (matchItem.consignee_address) payloadItem.consignee_address = matchItem.consignee_address;
            if (matchItem.invoice_number) payloadItem.invoice_number = matchItem.invoice_number;
            if (matchItem.dc_number) payloadItem.dc_number = matchItem.dc_number;
            if (matchItem.estimated_kms) payloadItem.estimated_kms = matchItem.estimated_kms;
          }
        }

        return payloadItem;
      });

      const start_lat = formData.start_latitude ? parseFloat(formData.start_latitude) : (parentTrip?.start_latitude || parentTrip?.rake_point?.latitude || 0);
      const start_lng = formData.start_longitude ? parseFloat(formData.start_longitude) : (parentTrip?.start_longitude || parentTrip?.rake_point?.longitude || 0);

      const payload = {
        movement: "Secondary",
        movementType: "ROAD",
        source_rake_movement_id: parentTripId,
        origin_rake_point_id: parentTrip?.rake_point_id,
        transporter_id: formData.transporter_id,
        driver_id: formData.driver_id,
        vehicle_id: formData.vehicle_id,
        start_latitude: start_lat,
        start_longitude: start_lng,
        items: itemsPayload,
        scheduled_start_time: new Date(formData.scheduled_start_time).toISOString(),
        scheduled_end_time: new Date(formData.scheduled_end_time).toISOString(),
      };

      const result = await tripService.createTrip(payload);

      toast({
        title: "Dispatch Created",
        description: `Successfully dispatched. Trip Number: ${result.trip_number}`,
      });

      if (onSuccess) {
        onSuccess(result);
      }
    } catch (err) {
      console.error(err);
      toast({
        title: "Error Creating Dispatch",
        description: err.message || "Failed to create child trip dispatch.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const getParentLineLabel = (item) => {
    const prodName = item.product_type?.name || "";
    const prodSuffix = prodName ? ` - ${prodName}` : "";

    if (item.distributor) {
      return `Distributor: ${item.distributor.company_name}${prodSuffix}`;
    } else if (item.retailer) {
      return `Retailer: ${item.retailer.company_name}${prodSuffix}`;
    } else if (item.rake_point) {
      return `Allocation: ${item.rake_point.name}${prodSuffix}`;
    }
    return `Allocation${prodSuffix || ` #${item.item_sequence || 1}`}`;
  };

  const parentLineOptions = (parentTrip?.items || [])
    .filter((item) => {
      const balanceLine = balanceData?.lines?.find((l) => l.rake_movement_item_id === item.id || l.trip_item_id === item.id);
      return !balanceLine || balanceLine.remaining_qty_mt > 0;
    })
    .map((item) => ({
      value: item.id,
      label: getParentLineLabel(item),
    }));

  const destinationTypeOptions = [
    { value: "distributor", label: "Distributor" },
    { value: "retailer", label: "Retailer" },
    { value: "godown", label: "Godown" },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader size="lg" message="Loading dispatch options..." />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      { }

      {errors.totalQty && (
        <div className="flex items-start gap-2 bg-destructive/10 border border-destructive/20 text-destructive p-3 rounded-lg text-xs">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span className="font-medium">{errors.totalQty}</span>
        </div>
      )}

      { }
      {/* Start Location Map */}
      <GoogleMapSelector
        title="Start Location *"
        onLocationSelect={(location) => {
          setFormData((prev) => ({
            ...prev,
            start_latitude: location.lat.toString(),
            start_longitude: location.lng.toString(),
          }));
        }}
        initialLat={formData.start_latitude ? parseFloat(formData.start_latitude) : null}
        initialLng={formData.start_longitude ? parseFloat(formData.start_longitude) : null}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="space-y-4 border border-border p-4 rounded-lg bg-card">
          <h3 className="text-sm font-medium text-foreground border-b pb-2 flex items-center gap-1.5 tracking-tight">
            <Truck className="w-4 h-4 text-primary" /> Assign Transporter, Driver & Vehicle
          </h3>
          <div className="space-y-4">
            <CommonSelect
              label="Transporter"
              id="transporter_id"
              value={formData.transporter_id}
              onValueChange={(val) => handleFormChange("transporter_id", val)}
              options={transporters.map((t) => ({ value: t.id, label: t.company_name || t.name }))}
              error={errors.transporter_id}
              required
              searchable
              onSearchChange={(s) => handleSearchChange("transporter", s)}
              searchValue={transporterSearch}
              placeholder="Select transporter"
              loading={loadingDropdowns.transporters}
            />

            <div className="grid grid-cols-2 gap-3">
              <CommonSelect
                label="Driver"
                id="driver_id"
                value={formData.driver_id}
                onValueChange={(val) => handleFormChange("driver_id", val)}
                options={drivers.map((d) => ({
                  value: d.id,
                  label: `${d.user?.username || d.name} (${d.license_number || "No License"})`,
                }))}
                error={errors.driver_id}
                required
                searchable
                onSearchChange={(s) => handleSearchChange("driver", s)}
                searchValue={driverSearch}
                placeholder="Select driver"
                loading={loadingDropdowns.drivers}
              />

              <CommonSelect
                label="Vehicle"
                id="vehicle_id"
                value={formData.vehicle_id}
                onValueChange={(val) => handleFormChange("vehicle_id", val)}
                options={vehicles.map((v) => ({ value: v.id, label: v.vehicle_number }))}
                error={errors.vehicle_id}
                required
                searchable
                onSearchChange={(s) => handleSearchChange("vehicle", s)}
                searchValue={vehicleSearch}
                placeholder="Select vehicle"
                loading={loadingDropdowns.vehicles}
              />
            </div>
          </div>
        </div>

        <div className="space-y-4 border border-border p-4 rounded-lg bg-card">
          <h3 className="text-sm font-medium text-foreground border-b pb-2 flex items-center gap-1.5 tracking-tight">
            <Calendar className="w-4 h-4 text-primary" /> Trip Schedule
          </h3>
          <div className="flex flex-col space-y-4">
            <DateTimeInput
              label="Scheduled Start"
              id="scheduled_start_time"
              value={formData.scheduled_start_time}
              onChange={(e) => handleFormChange("scheduled_start_time", e.target.value)}
              error={errors.scheduled_start_time}
              required
              minDate={new Date()}
              defaultToCurrentTime={true}
            />

            <DateTimeInput
              label="Scheduled End (ETA)"
              id="scheduled_end_time"
              value={formData.scheduled_end_time}
              onChange={(e) => handleFormChange("scheduled_end_time", e.target.value)}
              error={errors.scheduled_end_time}
              required
              minDate={new Date()}
            />
          </div>
        </div>
      </div>

      { }
      <div className="border border-border rounded-lg p-4 space-y-4 bg-secondary/5">
        <div className="flex justify-between items-center border-b pb-3 border-border/60">
          <h3 className="text-sm font-medium text-foreground flex items-center gap-1.5 tracking-tight">
            <FileText className="w-4.5 h-4.5 text-primary" /> Product & Destination Allocation
          </h3>
          <CommonButton
            type="button"
            variant="outline"
            size="sm"
            onClick={addItemRow}
            icon={Plus}
            className="border-primary text-primary hover:bg-primary/5 h-8.5 text-xs font-medium px-3.5"
          >
            Add Row item
          </CommonButton>
        </div>

        <div className="space-y-4">
          {items.map((row, idx) => {
            const rowErrors = errors.items?.[idx] || {};

            return (
              <SecondaryDispatchItemCard
                key={row.id}
                row={row}
                idx={idx}
                rowErrors={rowErrors}
                destinationTypeOptions={destinationTypeOptions}
                states={states}
                districtsByState={districtsByState}
                distributorsByDistrict={distributorsByDistrict}
                retailersByDistrict={retailersByDistrict}
                loadingDropdowns={loadingDropdowns}
                loadingDistricts={loadingDistricts}
                loadingDistributors={loadingDistributors}
                loadingRetailers={loadingRetailers}
                godowns={godowns}
                parentLineOptions={parentLineOptions}
                productTypes={productTypes}
                updateItemRow={updateItemRow}
                removeItemRow={removeItemRow}
                balanceData={balanceData}
                parentTrip={parentTrip}
                showDeleteButton={items.length > 1}
              />
            );
          })}
        </div>
      </div>

      <DialogFooter className="gap-2 border-t pt-4">
        <CommonButton type="button" variant="outline" onClick={onCancel} disabled={submitting}>
          Cancel
        </CommonButton>
        <CommonButton type="submit" loading={submitting}>
          Create Dispatch
        </CommonButton>
      </DialogFooter>
    </form>
  );
};

export default SecondaryDispatchForm;
